import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "wouter";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PasswordField } from "../components/PasswordField";
import { isStrongPassword, PASSWORD_REQUIREMENTS_MESSAGE, PASSWORD_MIN_LENGTH } from "../lib/password";
import { NameField } from "../components/NameField";
import { isValidName, NAME_REQUIREMENTS_MESSAGE } from "../lib/name";
import { existingAnalyticsBrowserKeyForSignup } from "../lib/analytics";
import { useI18n } from "../lib/i18n";
import { SignupLayout } from "../components/SignupLayout";
import { SignupActivityChoices } from "../components/SignupActivityChoices";

export default function Signup() {
  const [picked, setPicked] = useState<string[]>([]);
  const [compactLayout, setCompactLayout] = useState(() => !window.matchMedia("(min-width: 1280px)").matches);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", password: "" });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const createdAccount = useRef(false);
  const [, navigate] = useLocation();
  const { user, setUser } = useAuth();
  const { t } = useI18n();

  useEffect(() => {
    const media = window.matchMedia("(min-width: 1280px)");
    const updateLayout = () => setCompactLayout(!media.matches);
    media.addEventListener("change", updateLayout);
    updateLayout();
    return () => media.removeEventListener("change", updateLayout);
  }, []);

  useEffect(() => {
    // [account.responsive-setup](../../../docs/product/accounts.md#account-responsive-setup): the submit action owns a new account's setup destination;
    // the returning-member redirect must not race it after setUser.
    if (user && !createdAccount.current) navigate(user.userType === "admin" ? "/admin" : user.capabilities.length ? "/dashboard" : "/choose-activities", { replace: true });
  }, [user, navigate]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (!accepted) { setError("Accept the Terms of Service and Privacy Policy to continue."); return; }
    if (!isValidName(form.firstName) || !isValidName(form.lastName)) { setError(NAME_REQUIREMENTS_MESSAGE); return; }
    if (!isStrongPassword(form.password)) { setError(PASSWORD_REQUIREMENTS_MESSAGE); return; }
    // [account.signup](../../../docs/product/accounts.md#account-signup): confirmation prevents a mistyped signup password;
    // it stays client-side and is never included in the account request.
    if (form.password !== confirmPassword) {
      setError(t("auth.passwordMismatch", "Passwords do not match. Please re-enter your password."));
      document.getElementById("signup-confirm-password")?.focus();
      return;
    }
    setBusy(true);
    try {
      const analyticsBrowserKey = await existingAnalyticsBrowserKeyForSignup().catch(() => undefined);
      const u = await api.signup({
        ...form,
        // [consent.signup-acceptance](../../../docs/product/accounts.md#consent-signup-acceptance): the linked Terms page exposes the existing acknowledgements;
        // this explicit agreement replaces repeat acceptance in Welcome.
        onboardingConsent: { dataSharing: true, riskWaiver: true, accuracy: true },
        // [account.responsive-setup](../../../docs/product/accounts.md#account-responsive-setup): Find Work creates a usable account before optional compact activity setup.
        activities: !compactLayout && picked.length ? picked : ["find_work"],
        ...(analyticsBrowserKey ? { analyticsBrowserKey } : {}),
      });
      if (u) {
        createdAccount.current = true;
        setUser(u);
        navigate(compactLayout ? "/choose-activities" : "/welcome", { replace: true });
      }
      else setError("Could not create the account.");
    } catch (err: unknown) { setError(err instanceof Error ? err.message : "Signup failed."); }
    finally { setBusy(false); }
  }

  return (
    <SignupLayout plainMobile desktopActivities={<>
      <SignupActivityChoices picked={picked} busy={busy} onToggle={(activity) => setPicked((current) => current.includes(activity) ? current.filter((key) => key !== activity) : [...current, activity])} />
      <p className="mt-4 text-sm text-white/80" aria-live="polite">{picked.length ? `${picked.length} ${picked.length === 1 ? "activity" : "activities"} selected` : "Find Work is the default."}</p>
    </>}>
      <h1 id="signup-form-heading" className="mb-7 text-center text-[26px] font-semibold leading-tight tracking-tight text-navy xl:mb-8">Create your account</h1>
      <form className="space-y-3 md:space-y-6 [&_.field]:min-h-12 [&_.field]:text-base" onSubmit={submit}>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="mb-2 block text-[13px] font-semibold text-navy" htmlFor="signup-first-name">First name</label><NameField id="signup-first-name" required placeholder="Juan" value={form.firstName} onChange={(value) => { setForm({ ...form, firstName: value }); if (error) setError(null); }} /></div>
          <div><label className="mb-2 block text-[13px] font-semibold text-navy" htmlFor="signup-last-name">Last name</label><NameField id="signup-last-name" required placeholder="Dela Cruz" value={form.lastName} onChange={(value) => { setForm({ ...form, lastName: value }); if (error) setError(null); }} /></div>
        </div>
        <div><label className="mb-2 block text-[13px] font-semibold text-navy" htmlFor="signup-email">{t("auth.emailAddress", "Email")}</label><input id="signup-email" required type="email" className="field" placeholder="you@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div className="[&_ul]:grid [&_ul]:gap-x-3 [&_ul]:gap-y-1 [&_ul]:space-y-0 sm:[&_ul]:grid-cols-2">
          <label className="mb-2 block text-[13px] font-semibold text-navy" htmlFor="signup-password">{t("auth.password", "Password")}</label>
          <PasswordField id="signup-password" required minLength={PASSWORD_MIN_LENGTH} autoComplete="new-password" placeholder="Create a password" value={form.password} onChange={(value) => { setForm({ ...form, password: value }); if (error) setError(null); }} showRequirements={!compactLayout} />
          {compactLayout && <p className="mt-2 text-xs leading-5 text-mut">{PASSWORD_MIN_LENGTH}+ characters with uppercase, lowercase, a number and a symbol.</p>}
        </div>
        <div>
          <label className="mb-2 block text-[13px] font-semibold text-navy" htmlFor="signup-confirm-password">{t("auth.confirmPassword", "Confirm Password")}</label>
          <PasswordField id="signup-confirm-password" required autoComplete="new-password" placeholder={t("auth.reenterPassword", "Re-enter your password")} value={confirmPassword} onChange={(value) => { setConfirmPassword(value); if (error) setError(null); }} />
        </div>
        <label className="flex items-start gap-2 text-[13px] leading-5 text-mut"><input id="signup-acceptance" type="checkbox" required checked={accepted} onChange={(event) => { setAccepted(event.target.checked); setError(null); }} className="mt-0.5 h-4 w-4 shrink-0 accent-navy" /><span className="text-left">I agree to the <a href="/legal/terms" target="_blank" rel="noreferrer" className="text-brand-red hover:underline">Terms of Service</a> and <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-brand-red hover:underline">Privacy Policy</a>.</span></label>
        {error && <p className="text-sm text-brand-red" role="alert">{error}</p>}
        <div className="signup-actions grid grid-cols-2 gap-3 pt-1">
          <Link href="/login" className="btn-red min-h-12 rounded-full px-3 text-sm font-semibold">{t("auth.signup.login", "Login")}</Link>
          <button type="submit" disabled={busy} className="btn-outline min-h-12 rounded-full px-3 text-sm font-semibold disabled:opacity-60">{busy ? "Creating account…" : t("auth.login.signUp", "Sign Up")}</button>
        </div>
      </form>
    </SignupLayout>
  );
}
